import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { EventProvider } from "@/lib/event";
import { AttractionProvider } from "@/lib/attraction";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import { Toaster } from "@/components/ui/sonner";

import MarketingHome from "@/pages/MarketingHome";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import OnboardingPage from "@/pages/OnboardingPage";
import AuthCallback from "@/pages/AuthCallback";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

import DashboardOverview from "@/pages/DashboardOverview";
import OpenDaysPage from "@/pages/OpenDaysPage";
import TicketsPage from "@/pages/TicketsPage";
import TicketTypesPage from "@/pages/TicketTypesPage";
import EventWizardPage from "@/pages/EventWizardPage";
import MarketingPage from "@/pages/MarketingPage";
import GuestListsPage from "@/pages/GuestListsPage";
import GuestListDetailPage from "@/pages/GuestListDetailPage";
import GuestListSignupPage from "@/pages/GuestListSignupPage";
import SetupCompletePage from "@/pages/SetupCompletePage";
import EventDetailPage from "@/pages/EventDetailPage";
import OrdersListPage from "@/pages/OrdersListPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import AttendeesPage from "@/pages/AttendeesPage";
import CheckinPage, { KioskPage } from "@/pages/CheckinPage";
import ScanCheckinPage from "@/pages/ScanCheckinPage";
import EmailTemplatesPage from "@/pages/EmailTemplatesPage";
import ReportsPage from "@/pages/ReportsPage";
import ReachPage from "@/pages/ReachPage";
import SettingsPage from "@/pages/SettingsPage";
import BrandingPage from "@/pages/BrandingPage";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import SuperOverviewPage from "@/pages/super/SuperOverviewPage";
import SuperAttractionsPage from "@/pages/super/SuperAttractionsPage";
import SuperAttractionDetailPage from "@/pages/super/SuperAttractionDetailPage";
import SuperRevenuePage from "@/pages/super/SuperRevenuePage";
import SuperInterestPage from "@/pages/super/SuperInterestPage";
import SuperSettingsPage from "@/pages/super/SuperSettingsPage";

import PublicLandingPage from "@/pages/PublicLandingPage";
import PublicEventPage from "@/pages/PublicEventPage";
import PublicBookingFlow from "@/pages/PublicBookingFlow";
import TimeslotPreviewPage from "@/pages/TimeslotPreviewPage";
import FlowStepPreview from "@/pages/FlowStepPreview";
import ConfirmationPage from "@/pages/ConfirmationPage";
import TicketPage from "@/pages/TicketPage";
import OrderTicketsPage from "@/pages/OrderTicketsPage";
import PublicPaymentPage from "@/pages/PublicPaymentPage";

import StaffLoginPage from "@/pages/staff/StaffLoginPage";
import StaffApp from "@/pages/staff/StaffApp";
import StaffScanPage from "@/pages/staff/StaffScanPage";
import StaffSearchPage from "@/pages/staff/StaffSearchPage";

function Protected({ children, needsAttraction = true, needsSuper = false, withEvents = true }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#8a8a8a]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (needsSuper && !user.is_super_admin) return <Navigate to="/dashboard/overview" replace />;
  if (needsAttraction && !user.attraction_id && !user.impersonating_attraction_id) return <Navigate to="/onboarding" replace />;
  const inner = withEvents ? <AttractionProvider><EventProvider>{children}</EventProvider></AttractionProvider> : children;
  return inner;
}

function Router() {
  const loc = useLocation();
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<MarketingHome />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/onboarding" element={<Protected needsAttraction={false} withEvents={false}><OnboardingPage /></Protected>} />

      {/* Staff check-in PWA (separate PIN auth) */}
      <Route path="/app/login" element={<StaffLoginPage />} />
      <Route path="/app" element={<StaffApp />}>
        <Route index element={<StaffScanPage />} />
        <Route path="search" element={<StaffSearchPage />} />
      </Route>

      {/* Dashboard */}
      <Route path="/dashboard" element={<Navigate to="/dashboard/overview" replace />} />
      <Route path="/dashboard/overview" element={<Protected><DashboardOverview /></Protected>} />
      <Route path="/dashboard/open-days" element={<Protected><OpenDaysPage /></Protected>} />
      <Route path="/dashboard/tickets" element={<Protected><TicketsPage /></Protected>} />
      <Route path="/dashboard/tickets/types" element={<Protected><TicketTypesPage /></Protected>} />
      <Route path="/dashboard/marketing" element={<Protected><MarketingPage /></Protected>} />
      <Route path="/dashboard/guest-lists" element={<Protected><GuestListsPage /></Protected>} />
      <Route path="/dashboard/guest-lists/:id" element={<Protected><GuestListDetailPage /></Protected>} />
      <Route path="/dashboard/events/new" element={<Protected><EventWizardPage /></Protected>} />
      <Route path="/dashboard/events/:id/setup-complete" element={<Protected><SetupCompletePage /></Protected>} />
      <Route path="/dashboard/events/:id" element={<Protected><EventDetailPage /></Protected>} />
      <Route path="/dashboard/orders" element={<Protected><OrdersListPage /></Protected>} />
      <Route path="/dashboard/orders/:id" element={<Protected><OrderDetailPage /></Protected>} />
      <Route path="/dashboard/attendees" element={<Protected><AttendeesPage /></Protected>} />
      <Route path="/dashboard/checkin" element={<Protected><CheckinPage /></Protected>} />
      <Route path="/dashboard/checkin/scan" element={<Protected><ScanCheckinPage /></Protected>} />
      <Route path="/dashboard/checkin/kiosk" element={<Protected><KioskPage /></Protected>} />
      <Route path="/dashboard/emails" element={<Protected><EmailTemplatesPage /></Protected>} />
      <Route path="/dashboard/reports" element={<Protected><ReportsPage /></Protected>} />
      <Route path="/dashboard/reach" element={<Protected><ReachPage /></Protected>} />
      <Route path="/dashboard/settings" element={<Protected><SettingsPage /></Protected>} />
      <Route path="/dashboard/branding" element={<Protected><BrandingPage /></Protected>} />

      {/* Backwards-compatibility: old Events list URL → Open Days */}
      <Route path="/dashboard/events" element={<Navigate to="/dashboard/open-days" replace />} />

      {/* Super admin */}
      <Route path="/super" element={<Navigate to="/super-admin" replace />} />
      <Route path="/super/overview" element={<Navigate to="/super-admin" replace />} />
      <Route path="/super/attractions" element={<Navigate to="/super-admin/attractions" replace />} />
      <Route path="/super-admin" element={<Protected needsSuper needsAttraction={false} withEvents={false}><SuperAdminLayout /></Protected>}>
        <Route index element={<SuperOverviewPage />} />
        <Route path="attractions" element={<SuperAttractionsPage />} />
        <Route path="attractions/:id" element={<SuperAttractionDetailPage />} />
        <Route path="revenue" element={<SuperRevenuePage />} />
        <Route path="interest" element={<SuperInterestPage />} />
        <Route path="settings" element={<SuperSettingsPage />} />
      </Route>

      {/* Public tickets */}
      <Route path="/ticket/:id" element={<TicketPage />} />
      <Route path="/guest/:token" element={<GuestListSignupPage />} />
      <Route path="/order/:orderId/tickets" element={<OrderTicketsPage />} />
      <Route path="/pay/:token" element={<PublicPaymentPage />} />

      {/* Public booking */}
      <Route path="/booking-preview/:slug/timeslots" element={<TimeslotPreviewPage />} />
      <Route path="/booking-preview/:slug/tickets" element={<FlowStepPreview step="tickets" />} />
      <Route path="/booking-preview/:slug/checkout" element={<FlowStepPreview step="checkout" />} />
      <Route path="/booking-preview/:slug/confirmation" element={<FlowStepPreview step="confirmation" />} />
      <Route path="/booking-preview/:slug" element={<PublicEventPage preview />} />
      <Route path="/booking-preview/:slug/:eventSlug" element={<PublicEventPage preview />} />
      <Route path="/:slug" element={<PublicLandingPage />} />
      <Route path="/:slug/date/:dateId" element={<PublicBookingFlow />} />
      <Route path="/:slug/confirmation/:orderId" element={<ConfirmationPage />} />
      <Route path="/:slug/:eventSlug" element={<PublicEventPage />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ConfirmProvider>
            <Router />
            <Toaster position="top-right" richColors closeButton toastOptions={{ duration: 3000 }} />
          </ConfirmProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
